import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  Paper,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useModerationStore } from "../../store/moderationStore";
import { useModerationApi } from "./hooks/useModerationApi";
import { useGlobalLoading } from "../../hooks/useGlobalLoading";
import { useSnackbar } from "../../hooks/useSnackbar";

interface AllowlistSectionProps {
  onRefresh: () => void;
}

export const AllowlistSection: React.FC<AllowlistSectionProps> = ({
  onRefresh,
}) => {
  const api = useModerationApi();
  const { withLoading } = useGlobalLoading();
  const { allowlist, loadAllowlist } = useModerationStore();

  const [newWord, setNewWord] = useState("");
  const { showSnackbar, hideSnackbar, snackbar } = useSnackbar();

  const handleAddWord = async () => {
    if (!newWord.trim()) {
      showSnackbar("Please enter a word", "error");
      return;
    }

    try {
      await withLoading(api.addAllowlistWord(newWord.trim()));
      await loadAllowlist();
      showSnackbar("Word added to allowlist", "success");
      setNewWord("");
    } catch (error: any) {
      showSnackbar(error.message || "Failed to add word", "error");
    }
  };

  const handleDeleteWord = async (word: string) => {
    try {
      await withLoading(api.deleteAllowlistWord(word));
      await loadAllowlist();
      showSnackbar("Word removed from allowlist", "success");
    } catch (error: any) {
      showSnackbar(error.message || "Failed to remove word", "error");
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Allowlist Management
      </Typography>

      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          sx={{ mb: 2 }}
          onClose={hideSnackbar}
        >
          {snackbar.message}
        </Alert>
      )}

      {/* Add new word */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Add Word to Allowlist
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 9 }}>
            <TextField
              fullWidth
              label="Word"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              placeholder="Enter word to allow"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleAddWord();
                }
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 3 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddWord}
              sx={{
                height: "56px",
                backgroundColor: "#7357FF",
                "&:hover": { backgroundColor: "#5a42cc" },
              }}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Display allowlist */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Allowed Words
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {!allowlist?.words || allowlist.words.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No words in allowlist
            </Typography>
          ) : (
            allowlist.words.map((word) => (
              <Chip
                key={word}
                label={word}
                onDelete={() => handleDeleteWord(word)}
                deleteIcon={<DeleteIcon />}
                color="success"
                sx={{ fontSize: "0.9rem" }}
              />
            ))
          )}
        </Box>
      </Paper>
    </Box>
  );
};
